import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useHistory, useParams } from 'react-router-dom'
import { FormControl, Grid, makeStyles } from '@material-ui/core'
import { countries } from '@klarpay/enums'

import {
  CountryPhoneSelect,
  FormAutocompleteSelect,
  FormControlledTextField,
} from '../../../components'
import {
  accountNoTypeCountries,
  accountNoTypeList,
  clearingMemberIdCountries,
  creditorAgentBranchIdCountries,
  creditorAgentBranchNameCountries,
  creditorContractPhoneCountries,
  creditorTaxIdCountries,
} from '../../../stubs'

import { APP_PATHS, PATH_PARAMS } from '../../../routes/paths'
import clsx from 'clsx'
import { AccountIdTypeEnum } from '../../../types'

const useStyles = (isCreateAccountPage: boolean) =>
  makeStyles((theme) => ({
    container: {
      '& :first-child': {
        borderRadius: '4px 4px 0px 0px',
      },
      '& :last-child': {
        marginBottom: 0,
      },
    },
    formControl: {
      width: '100%',
      '& .MuiInputBase-input:-webkit-autofill': {
        boxShadow: '0 0 0 42px white inset !important',
        webkitBoxShadow: '0 0 0 42px white inset !important',
      },
      '& .MuiInputBase-input:-webkit-autofill:hover': {
        boxShadow: '0 0 0 42px white inset !important',
        webkitBoxShadow: '0 0 0 42px white inset !important',
      },
      '& .MuiInputBase-input:-webkit-autofill:focus': {
        boxShadow: '0 0 0 42px white inset !important',
        webkitBoxShadow: '0 0 0 42px white inset !important',
      },
      '& .MuiInputBase-input:-webkit-autofill:active': {
        boxShadow: '0 0 0 42px white inset !important',
        webkitBoxShadow: '0 0 0 42px white inset !important',
      },
      '& .MuiInputBase-input:-internal-autofill-selected': {
        webkitBoxShadow: '0 0 0 42px white inset !important',
        boxShadow: '0 0 0 42px white inset !important',
      },
      '& .MuiInputAdornment-positionEnd': {
        position: 'absolute',
        right: 15,
      },
    },
    whiteBackGround: {
      width: '100%',
      background: '#FFFFFF',
      borderRadius: '4px 4px 0px 0px',
      '& .MuiFormHelperText-root': {
        paddingLeft: 0,
      },
      '& p#swift-helper-text, & p#postalCode-helper-text': {
        whiteSpace: 'nowrap',
      },
    },
    fontModify: {
      '& > label': {
        [theme.breakpoints.down('sm')]: {
          fontSize: '0.75rem',
          whiteSpace: 'nowrap',
        },
      },
    },
    '@global': {
      '#prefixCode': {
        paddingTop: isCreateAccountPage ? 0 : 'inherit',
      },
    },
  }))()

const SpecificFields: FC<{
  country: keyof typeof countries
  defaultCountryValue?: keyof typeof countries
  accountIdType?: AccountIdTypeEnum
}> = ({ country, defaultCountryValue, accountIdType }) => {
  const history = useHistory()
  const { [PATH_PARAMS.applicationId]: applicationId } = useParams() as Record<string, string>
  const isCreateAccountPage =
    history.location.pathname ===
    generatePath(APP_PATHS.dashboard.createAccount, {
      [PATH_PARAMS.applicationId]: applicationId,
    })
  const classes = useStyles(isCreateAccountPage)
  const { t } = useTranslation()
  const countryCheckCreditorPhone = creditorContractPhoneCountries.includes(country)
  const creditorIdCheck = creditorTaxIdCountries.includes(country)
  const memberIdCheck =
    clearingMemberIdCountries.includes(country) && accountIdType === AccountIdTypeEnum.accountNumber
  const agentBranchIdCheck = creditorAgentBranchIdCountries.includes(country)
  const agentBranchNameCheck = creditorAgentBranchNameCountries.includes(country)
  const accountNoTypeCheck = accountNoTypeCountries.includes(country)
  const anyCheckPassed =
    countryCheckCreditorPhone ||
    creditorIdCheck ||
    memberIdCheck ||
    agentBranchIdCheck ||
    agentBranchNameCheck ||
    accountNoTypeCheck

  return (
    <>
      {anyCheckPassed && (
        <Grid item xs={12} className={classes.container}>
          {countryCheckCreditorPhone && (
            <CountryPhoneSelect
              className={clsx(classes.whiteBackGround, classes.fontModify)}
              name="creditorContractPhone"
              label={t('beneficiaryContactPhone', 'Beneficiary Contact Phone')}
              autoClear={country !== defaultCountryValue}
              countryDefault={country || defaultCountryValue}
              skipToastMessage
            />
          )}
          {creditorIdCheck && (
            <FormControl className={classes.formControl}>
              <FormControlledTextField
                className={classes.whiteBackGround}
                label={t('beneficiaryTaxId', 'Beneficiary Tax ID')}
                name="creditorTaxId"
                type="text"
                fullWidth
                required={false}
              />
            </FormControl>
          )}
          {accountNoTypeCheck && (
            <FormControl fullWidth>
              <FormAutocompleteSelect
                label={t('accountType', 'Account type')}
                name="accountNoType"
                data={accountNoTypeList}
              />
            </FormControl>
          )}
          {memberIdCheck && (
            <FormControl className={classes.formControl}>
              <FormControlledTextField
                className={classes.whiteBackGround}
                label={t('routingCode', 'Sort / BSB / Routing Code')}
                name="memberId"
                type="text"
                fullWidth
                required={false}
              />
            </FormControl>
          )}
          {agentBranchIdCheck && (
            <FormControl className={classes.formControl}>
              <FormControlledTextField
                className={classes.whiteBackGround}
                label={t('agentBranchId', 'Beneficiary Agent Branch ID')}
                name="agentBranchId"
                type="text"
                fullWidth
                required={false}
              />
            </FormControl>
          )}
          {agentBranchNameCheck && (
            <FormControl className={classes.formControl}>
              <FormControlledTextField
                className={classes.whiteBackGround}
                label={t('agentBranchName', 'Beneficiary Agent Branch Name')}
                name="agentBranchName"
                type="text"
                fullWidth
                required={false}
              />
            </FormControl>
          )}
        </Grid>
      )}
    </>
  )
}
export const SpecificRequiredFields = React.memo(SpecificFields)
